import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, AlertCircle, CheckCircle, Loader2, Archive, Zap } from 'lucide-react';
import { formatFileSize, formatProgress, formatTimeRemaining, getFileTypeIcon, getCompressionStatus } from '../utils/formatters';
import { uploadApi } from '../api/upload';

// Extended upload status for staging and background uploads
enum ExtendedUploadStatus {
  PENDING = 'PENDING',
  STAGING = 'STAGING', // Pre-uploading to Azure (not committed to database)
  STAGED = 'STAGED', // Pre-uploaded to Azure, awaiting user decision
  COMMITTING = 'COMMITTING', // User confirmed, committing to database
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}
import toast from 'react-hot-toast';
import { useNotifications } from '../hooks/useNotifications';
import { validateUploadedFile, formatValidationResult } from '../utils/sqlServerValidation';
import { processFileForUpload, CompressionResult, CompressionProgress } from '../utils/compression';

interface UploadFile {
  // File properties
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  // Store original File object for uploads
  originalFile: File;
  
  // Upload properties
  progress: number;
  status: ExtendedUploadStatus;
  uploadId?: string;
  estimatedTimeRemaining?: number;
  error?: string;
  // Compression info
  compressionProgress?: number;
  processingStage?: string;
  compressionInfo?: {
    isCompressing: boolean;
    wasOriginallyZip: boolean;
    originalSize?: number;
    estimatedCompressedSize?: number;
  };
  // Final compression results
  isCompressed?: boolean;
  wasOriginallyZip?: boolean;
  originalSize?: number;
  compressionRatio?: number;
  spaceSaved?: string;
  // Staging properties
  azureFileName?: string; // Name of file in Azure (for cleanup if not committed)
  isStaged?: boolean; // Whether file was pre-uploaded to Azure
  
  // Direct SAS upload properties
  sasUrl?: string; // SAS URL for direct blob upload
  blobName?: string; // Azure blob name for direct upload
  expiresAt?: Date; // When SAS URL expires
  useDirect?: boolean; // Whether to use direct blob upload (bypasses App Service timeout)
  
  // Frontend compression properties
  processedFile?: File; // The processed (potentially compressed) file for upload
  compressionResult?: CompressionResult; // Results from frontend compression
}

export const UploadPage = () => {
  const { showInfo, showSuccess, showError } = useNotifications();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [concurrentUploads, setConcurrentUploads] = useState<Set<string>>(new Set());
  const [showUploads, setShowUploads] = useState(false); // Control visibility of upload progress
  const [stagedFiles, setStagedFiles] = useState<Set<string>>(new Set()); // Track files staged in Azure for cleanup
  const [userWantsToCommit, setUserWantsToCommit] = useState(false); // Track if user clicked Upload button
  const userWantsToCommitRef = useRef(false); // Ref version that won't be affected by closures
  const filesRef = useRef<UploadFile[]>([]); // Ref to always have latest files state
  const useDirectUpload = true; // Always use intelligent upload method selection
  const [autoStartUploads, setAutoStartUploads] = useState(true); // Automatically start uploads when files are added

  const maxFileSize = parseInt((import.meta as any).env.VITE_MAX_FILE_SIZE_BYTES || '10737418240'); // 10GB
  const maxConcurrentUploads = 2; // Limit concurrent uploads to prevent overwhelming the server
  const directUploadThreshold = 100 * 1024 * 1024; // 100MB - use direct upload for files larger than this

  // Keep filesRef updated with latest files state for cleanup functions
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {

    
    // Handle rejected files
    rejectedFiles.forEach((rejectedFile) => {
      const { file, errors } = rejectedFile;
      errors.forEach((error: any) => {
        if (error.code === 'file-too-large') {
          toast.error(`File "${file.name}" is too large. Maximum size is ${formatFileSize(maxFileSize)}.`);
        } else {
          toast.error(`File "${file.name}" was rejected: ${error.message}`);
        }
      });
    });

    // Validate SQL Server backup files
    const validatedFiles: UploadFile[] = [];
    
    for (const file of acceptedFiles) {
      console.log('onDrop - Validating file:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      
      try {
        // Show validation progress
        showInfo(`Validating ${file.name}...`);
        
        const validationResult = await validateUploadedFile(file, {
          allowZip: true,
          maxZipFiles: 50,
          maxZipUncompressedSize: 25 * 1024 * 1024 * 1024 // 25GB
        });
        
        const resultMessage = formatValidationResult(validationResult);
        
        if (validationResult.isValid) {
          console.log('onDrop - File validation successful:', {
            filename: file.name,
            fileType: validationResult.fileType,
            validatedFiles: validationResult.validatedFiles
          });
          
          showSuccess(resultMessage);
          
          // Create upload file object
          validatedFiles.push({
            // File properties
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            originalFile: file, // Store the original File object
            
            // Upload properties
            progress: 0,
            status: ExtendedUploadStatus.PENDING,
          });
        } else {
          console.error('onDrop - File validation failed:', {
            filename: file.name,
            error: validationResult.error
          });
          
          showError(resultMessage);
        }
      } catch (error) {
        console.error('onDrop - Validation error:', error);
        showError(`Failed to validate ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    if (validatedFiles.length > 0) {
      console.log('onDrop - Adding validated files:', validatedFiles);
      setFiles((prev) => [...prev, ...validatedFiles]);
    }
    
    // Note: Background staging will be triggered after this callback completes
  }, [maxFileSize, showInfo, showSuccess, showError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    multiple: true,
    accept: {
      'application/zip': ['.zip'],
      'application/octet-stream': ['.bak'],
      'application/x-sql-backup': ['.bak']
    },
  });

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const cancelUpload = (fileId: string) => {
    // Remove from concurrent uploads tracking
    setConcurrentUploads(prev => {
      const newSet = new Set(prev);
      newSet.delete(fileId);
      return newSet;
    });

    // Update file status to cancelled (we'll treat it as failed for UI purposes)
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId
          ? { ...f, status: ExtendedUploadStatus.FAILED, error: 'Upload cancelled by user' }
          : f
      )
    );
  };

  // Removed startNextUpload function to prevent upload loops
  // Uploads are now triggered only by user actions or file additions

  // Auto-start staging when new files are added (background pre-upload)
  const autoStartStaging = useCallback(() => {
    // Only auto-start uploads if the setting is enabled
    if (!autoStartUploads) {
      console.log('Auto-start uploads disabled, skipping automatic upload');
      return;
    }

    const slotsAvailable = maxConcurrentUploads - concurrentUploads.size;
    const pendingFiles = files.filter(
      f => f.status === ExtendedUploadStatus.PENDING && !concurrentUploads.has(f.id)
    ).slice(0, slotsAvailable);

    pendingFiles.forEach(file => {
      setConcurrentUploads(prev => new Set([...prev, file.id]));
      
      // Choose upload method based on file size and user preference
      const shouldUseDirect = file.useDirect || (useDirectUpload && file.size > directUploadThreshold);
      
      if (shouldUseDirect) {
        console.log('Using direct upload for large file:', file.name, 'Size:', file.size);
        uploadFileDirectly(file).finally(() => {
          setConcurrentUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(file.id);
            return newSet;
          });
          // Don't automatically chain uploads to prevent loops
        });
      } else {
        console.log('Using staging upload for file:', file.name, 'Size:', file.size);
        stageFile(file).finally(() => {
          setConcurrentUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(file.id);
            return newSet;
          });
          // Don't automatically chain uploads to prevent loops
        });
      }
    });
  }, [files, concurrentUploads, maxConcurrentUploads, useDirectUpload, directUploadThreshold, autoStartUploads]);

  // Commit staged files when user clicks Upload button
  const commitAndShowUploads = () => {
    console.log('DEBUG - commitAndShowUploads called');
    setShowUploads(true);
    setUserWantsToCommit(true);
    userWantsToCommitRef.current = true;
    console.log('DEBUG - setUserWantsToCommit(true) and ref set to true');
    
    // Commit any files that are already staged
    const currentStagedFiles = files.filter(f => f.status === ExtendedUploadStatus.STAGED);
    console.log('DEBUG - currentStagedFiles:', currentStagedFiles.length);
    if (currentStagedFiles.length > 0) {
      commitStagedFiles();
    }
    
    // Files that are still staging will auto-commit when they finish
    const stillStagingFiles = files.filter(f => f.status === ExtendedUploadStatus.STAGING);
    console.log('DEBUG - stillStagingFiles:', stillStagingFiles.length);
    if (stillStagingFiles.length > 0) {
      showInfo('Upload Queued', `${stillStagingFiles.length} file${stillStagingFiles.length !== 1 ? 's' : ''} will upload automatically when staging completes`);
    }
  };

  // Trigger background uploads when new files are added
  useEffect(() => {
    if (!showUploads && files.length > 0) {
      const pendingFilesCount = files.filter(f => f.status === ExtendedUploadStatus.PENDING).length;
      const availableSlots = maxConcurrentUploads - concurrentUploads.size;
      
      if (pendingFilesCount > 0 && availableSlots > 0) {
        autoStartStaging();
      }
    }
  }, [files.length, showUploads, maxConcurrentUploads, concurrentUploads.size, autoStartStaging]);

  // Warn user before leaving page with active uploads and cleanup staged files
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (concurrentUploads.size > 0 || stagedFiles.size > 0) {
        e.preventDefault();
        e.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
        
        // Clean up ONLY uncommitted staged files when user leaves
        const filesToPotentiallyCleanup = Array.from(stagedFiles).filter(azureFileName => {
          const fileInState = filesRef.current.find(f => f.azureFileName === azureFileName);
          return !fileInState || fileInState.status !== ExtendedUploadStatus.COMPLETED;
        });

        if (filesToPotentiallyCleanup.length > 0) {
          cleanupStagedFiles(filesToPotentiallyCleanup);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Clean up ONLY uncommitted staged files when component unmounts
      const filesToPotentiallyCleanup = Array.from(stagedFiles).filter(azureFileName => {
        const fileInState = filesRef.current.find(f => f.azureFileName === azureFileName);
        console.log('DEBUG: Checking file for cleanup:', azureFileName, 'found in state:', !!fileInState, 'status:', fileInState?.status);
        return !fileInState || fileInState.status !== ExtendedUploadStatus.COMPLETED;
      });

      if (filesToPotentiallyCleanup.length > 0) {
        console.log('DEBUG: useEffect cleanup - Calling cleanupStagedFiles for uncommitted files. Files:', filesToPotentiallyCleanup);
        cleanupStagedFiles(filesToPotentiallyCleanup);
      } else {
        console.log('DEBUG: useEffect cleanup - No uncommitted staged files to cleanup.');
      }
    };
  }, [concurrentUploads.size]); // Using filesRef.current instead of closure to get latest file statuses

  // Removed old upload functions - using staging system now

  // Direct blob upload - bypasses App Service timeout for large files
  const uploadFileDirectly = async (file: UploadFile) => {
    try {
      console.log('uploadFileDirectly - Starting direct upload for file:', {
        id: file.id,
        name: file.name,
        size: file.size,
        useDirect: file.useDirect
      });

      // Step 1: Compress file if needed - start showing progress immediately
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { 
              ...f, 
              status: ExtendedUploadStatus.STAGING, 
              processingStage: 'Analyzing file...', 
              progress: 0,
              compressionProgress: 0,
              compressionInfo: {
                isCompressing: true,
                wasOriginallyZip: false,
                originalSize: f.size,
                estimatedCompressedSize: undefined
              }
            }
          : f
      ));

      const compressionResult = await processFileForUpload(
        file.originalFile,
        (progress: CompressionProgress) => {
          setFiles(prev => prev.map(f => 
            f.id === file.id 
              ? { 
                  ...f, 
                  compressionProgress: progress.progress,
                  processingStage: progress.stage,
                  progress: Math.round(progress.progress * 0.3) // Compression takes 30% of total progress
                }
              : f
          ));
        }
      );

      // Update file with compression results
      const processedFile = compressionResult.file;
      const finalFileName = compressionResult.isCompressed ? processedFile.name : file.name;
      
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { 
              ...f, 
              processedFile,
              compressionResult,
              name: finalFileName, // Update name if compressed
              size: compressionResult.compressedSize, // Update size to compressed size
              isCompressed: compressionResult.isCompressed,
              wasOriginallyZip: compressionResult.wasOriginallyZip,
              compressionRatio: compressionResult.compressionRatio,
              spaceSaved: compressionResult.isCompressed ? 
                `${((1 - compressionResult.compressionRatio) * 100).toFixed(1)}%` : undefined,
              processingStage: 'Getting upload URL...',
              progress: 30,
              compressionProgress: 100,
              compressionInfo: {
                isCompressing: false,
                wasOriginallyZip: compressionResult.wasOriginallyZip,
                originalSize: f.size,
                estimatedCompressedSize: compressionResult.compressedSize
              }
            }
          : f
      ));

      console.log('uploadFileDirectly - Compression completed:', {
        originalName: file.name,
        finalName: finalFileName,
        originalSize: file.size,
        compressedSize: compressionResult.compressedSize,
        isCompressed: compressionResult.isCompressed,
        compressionRatio: compressionResult.compressionRatio
      });

      // Step 2: Get SAS URL from backend
      const sasResponse = await uploadApi.getSASUpload(
        finalFileName, // Use final filename (potentially with .zip extension)
        compressionResult.compressedSize, // Use compressed size
        processedFile.type || 'application/octet-stream',
        30 // 30 minutes expiration
      );

      if (!sasResponse.success || !sasResponse.data) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadId, blobName, sasUrl, expiresAt } = sasResponse.data;

      // Update file with SAS info
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { 
              ...f, 
              uploadId,
              blobName,
              sasUrl,
              expiresAt: new Date(expiresAt),
              status: ExtendedUploadStatus.STAGING,
              processingStage: 'Uploading directly to cloud storage...'
            }
          : f
      ));

      console.log('uploadFileDirectly - Got SAS URL:', {
        uploadId,
        blobName,
        expiresAt
      });

      // Step 3: Upload through backend proxy (solves VNet issues)
      const uploadSuccess = await uploadApi.uploadToBlobDirect(
        sasUrl, // This is now ignored since we're using backend proxy
        processedFile, // Use the processed (potentially compressed) file
        (progress) => {
          // Upload progress is 30% to 90% of total progress (compression was 0-30%)
          const totalProgress = 30 + Math.round(progress * 0.6);
          const stage = `Uploading through backend: ${progress.toFixed(1)}%`;
            
          setFiles(prev => prev.map(f => 
            f.id === file.id 
              ? { 
                  ...f, 
                  progress: totalProgress,
                  bytesUploaded: Math.round((progress / 100) * processedFile.size),
                  processingStage: stage
                }
              : f
          ));
        }
      );

      if (!uploadSuccess) {
        throw new Error('Backend proxy upload failed');
      }

      console.log('uploadFileDirectly - Backend proxy upload completed successfully');

      // Update final status
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { 
              ...f, 
              status: ExtendedUploadStatus.COMPLETED,
              progress: 100,
              processingStage: 'Upload completed successfully'
            }
          : f
      ));

      console.log('uploadFileDirectly - Upload completed successfully:', {
        uploadId,
        fileId: file.id
      });

      showSuccess(`File "${file.name}" uploaded successfully using backend proxy!`);

    } catch (error) {
      console.error('uploadFileDirectly - Upload failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      setFiles(prev => prev.map(f => 
        f.id === file.id 
          ? { 
              ...f, 
              status: ExtendedUploadStatus.FAILED,
              error: errorMessage,
              processingStage: 'Upload failed'
            }
          : f
      ));

      showError(`Failed to upload "${file.name}": ${errorMessage}`);
      throw error;
    }
  };

  // Visible upload - shows progress to user
  const uploadFileVisibly = async (file: UploadFile) => {
    // Choose upload method based on file size and user preference
    if (file.useDirect || (useDirectUpload && file.size > directUploadThreshold)) {
      return uploadFileDirectly(file);
    } else {
      return uploadFileInternal(file, false);
    }
  };

  const uploadFileInternal = async (file: UploadFile, isBackgroundMode: boolean) => {
    try {
      console.log('uploadFile - Starting upload for file:', {
        id: file.id,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file
      });
      
      // Show timing expectation for large files
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 500) { // Show for files larger than 500MB
        const estimatedMinutes = Math.ceil(fileSizeInMB / 100);
        showInfo(
          'Large File Upload', 
          `Uploading ${formatFileSize(file.size)} file. This may take ${estimatedMinutes}+ minutes.`,
          { duration: 8000 }
        );
      }
      
      // Update file status to staging (this function is legacy, should use stageFile instead)
      const newStatus = ExtendedUploadStatus.STAGING;
        
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: newStatus, progress: 0 } : f
        )
      );

      console.log('uploadFile - Calling initiateUpload with:', {
        filename: file.name,
        size: file.size,
        mimeType: file.type
      });

      // Initiate upload session
      const initiateResponse = await uploadApi.initiateUpload(
        file.name,
        file.size,
        file.type
      );

      if (!initiateResponse.success || !initiateResponse.data) {
        throw new Error('Failed to initiate upload');
      }

      const { uploadId } = initiateResponse.data;

      // Update file with upload ID
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, uploadId } : f
        )
      );

      // Start file upload with progress tracking
      const startTime = Date.now();
      
      const uploadResponse = await uploadApi.uploadFile(
        uploadId,
        file.originalFile, // Use the original File object
        async (progress) => {
          const elapsedTime = Date.now() - startTime;
          const estimatedTimeRemaining = progress > 0 
            ? Math.round((elapsedTime / progress) * (100 - progress) / 1000)
            : 0;

          // Only update progress UI if in visible mode
          if (!isBackgroundMode) {
            // Get progress updates from API (cookies are automatically sent)
            const progressResponse = await uploadApi.getUploadProgress(uploadId);
            if (progressResponse.success && progressResponse.data) {
              const apiProgress = progressResponse.data;
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === file.id
                    ? {
                        ...f,
                        progress: apiProgress.percentage,
                        estimatedTimeRemaining: apiProgress.estimatedTimeRemaining,
                        compressionProgress: apiProgress.compressionProgress,
                        processingStage: apiProgress.processingStage,
                        compressionInfo: apiProgress.compressionInfo,
                      }
                    : f
                )
              );
            } else {
              // Fallback to calculated progress
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === file.id
                    ? {
                        ...f,
                        progress,
                        estimatedTimeRemaining,
                      }
                    : f
                )
              );
            }
          }
        }
      );

      if (uploadResponse.success && uploadResponse.data) {
        // Upload completed successfully
        const result = uploadResponse.data;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === file.id
              ? {
                  ...f,
                  status: ExtendedUploadStatus.COMPLETED,
                  progress: 100,
                  estimatedTimeRemaining: 0,
                  processingStage: 'Completed',
                  // Save compression results
                  isCompressed: result.isCompressed,
                  wasOriginallyZip: result.wasOriginallyZip,
                  originalSize: result.originalSize,
                  compressionRatio: result.compressionRatio,
                  spaceSaved: result.spaceSaved,
                }
              : f
          )
        );

        const compressionMessage = result.isCompressed 
          ? ` (compressed - ${result.spaceSaved} saved)`
          : result.wasOriginallyZip 
          ? ' (already compressed)'
          : '';
        
        toast.success(`Successfully uploaded ${file.name}${compressionMessage}`);
      } else {
        throw new Error(uploadResponse.error?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      
      // Provide more specific error messages for timeouts
      let errorMessage = error.message || 'Upload failed';
      if (error.code === 'ECONNABORTED' || errorMessage.includes('timeout')) {
        errorMessage = `Upload timed out. Large files (${formatFileSize(file.size)}) may take several minutes. Please retry.`;
      }
      
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? {
                ...f,
                status: ExtendedUploadStatus.FAILED,
                error: errorMessage,
              }
            : f
        )
      );

      toast.error(`Failed to upload ${file.name}: ${errorMessage}`);
    }
  };

  const retryFailedFile = async (file: UploadFile) => {
    if (file.status === ExtendedUploadStatus.FAILED) {
      // Reset file to pending status
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: ExtendedUploadStatus.PENDING, error: undefined } : f
        )
      );
      
      // If uploads are visible, start immediately, otherwise start in background
      if (showUploads) {
        if (concurrentUploads.size < maxConcurrentUploads) {
          setConcurrentUploads(prev => new Set([...prev, file.id]));
          uploadFileVisibly(file).finally(() => {
            setConcurrentUploads(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.id);
              return newSet;
            });
          });
        }
      } else {
        setTimeout(() => autoStartStaging(), 100);
      }
    }
  };

  const clearCompleted = () => {
    setFiles((prev) => prev.filter((file) => file.status !== ExtendedUploadStatus.COMPLETED));
  };

  // Staging functions for pre-upload without database commit
  const stageFile = async (file: UploadFile): Promise<void> => {
    try {
      console.log('stageFile - Starting staging for file:', file);
      
      // Update file status to staging
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: ExtendedUploadStatus.STAGING, progress: 0 } : f
        )
      );

      // Initiate upload session
      const initiateResponse = await uploadApi.initiateUpload(
        file.name,
        file.size,
        file.type
      );

      if (!initiateResponse.success || !initiateResponse.data) {
        throw new Error('Failed to initiate staging');
      }

      const { uploadId } = initiateResponse.data;

      // Update file with upload ID
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, uploadId } : f
        )
      );

      // Stage file (upload to Azure but don't save to database)
      const stageResponse = await uploadApi.stageFile(
        uploadId,
        file.originalFile,
        (progress) => {
          // Update progress silently for staging
          setFiles((prev) =>
            prev.map((f) =>
              f.id === file.id ? { ...f, progress } : f
            )
          );
        }
      );

      if (stageResponse.success && stageResponse.data) {
        // File staged successfully
        const result = stageResponse.data;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === file.id
              ? {
                  ...f,
                  status: ExtendedUploadStatus.STAGED,
                  progress: 100,
                  azureFileName: result.azureFileName,
                  isStaged: true,
                  // Keep the uploadId from the previous state
                  uploadId: f.uploadId,
                  // Save compression results
                  isCompressed: result.isCompressed,
                  wasOriginallyZip: result.wasOriginallyZip,
                  compressionRatio: result.compressionRatio,
                  spaceSaved: result.spaceSaved,
                }
              : f
          )
        );

        // Track staged file for cleanup
        setStagedFiles(prev => new Set([...prev, result.azureFileName]));
        
        console.log('File staged successfully:', result.azureFileName);
        console.log('DEBUG - userWantsToCommit state:', userWantsToCommit);
        console.log('DEBUG - userWantsToCommitRef.current:', userWantsToCommitRef.current);
        
        // If user clicked Upload, auto-commit this file now that it's staged
        if (userWantsToCommitRef.current) {
          console.log('User wants to commit - auto-committing staged file:', file.name);
          
          // Wait for React state to update, then get the current file data
          setTimeout(() => {
            setFiles((currentFiles) => {
              const currentFile = currentFiles.find(f => f.id === file.id);
              if (currentFile && currentFile.status === ExtendedUploadStatus.STAGED && currentFile.uploadId && currentFile.azureFileName) {
                console.log('Auto-committing file with uploadId:', currentFile.uploadId);
                commitSingleFile(currentFile);
              } else {
                console.error('Cannot auto-commit - missing data:', {
                  found: !!currentFile,
                  status: currentFile?.status,
                  uploadId: currentFile?.uploadId,
                  azureFileName: currentFile?.azureFileName
                });
              }
              return currentFiles; // No state change, just accessing current state
            });
          }, 200);
        }
      } else {
        throw new Error(stageResponse.error?.message || 'Staging failed');
      }
    } catch (error: any) {
      console.error('Staging failed:', error);
      
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id
            ? {
                ...f,
                status: ExtendedUploadStatus.FAILED,
                error: error.message || 'Staging failed',
              }
            : f
        )
      );
      
      showError('Staging Failed', `Failed to stage ${file.name}: ${error.message}`);
    }
  };

  // Commit a single file
  const commitSingleFile = async (file: UploadFile): Promise<void> => {
    console.log('commitSingleFile - processing file:', { 
      name: file.name, 
      uploadId: file.uploadId, 
      azureFileName: file.azureFileName 
    });
    
    if (!file.uploadId || !file.azureFileName) {
      console.error('Missing uploadId or azureFileName for file:', file);
      showError('Commit Error', `Missing upload data for ${file.name}. Please try re-uploading.`);
      return;
    }

    try {
      // Update status to committing
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: ExtendedUploadStatus.COMMITTING } : f
        )
      );

      // Commit staged file to database
      const commitResponse = await uploadApi.commitStagedFile(file.uploadId, file.azureFileName);

      if (commitResponse.success && commitResponse.data) {
        // File committed successfully
        const result = commitResponse.data;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === file.id
              ? {
                  ...f,
                  status: ExtendedUploadStatus.COMPLETED,
                  isStaged: false,
                }
              : f
          )
        );

        // Remove from staged files tracking now that commit succeeded
        setStagedFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(file.azureFileName!);
          return newSet;
        });

        const compressionMessage = result.isCompressed 
          ? ` (compressed - ${result.spaceSaved} saved)`
          : result.wasOriginallyZip 
          ? ' (already compressed)'
          : '';
        
        showSuccess('Upload Complete', `Successfully uploaded ${file.name}${compressionMessage}`);
        
        // Check if all files are now completed, if so reset the commit intent
        const remainingFiles = files.filter(f => 
          f.status === ExtendedUploadStatus.STAGING || 
          f.status === ExtendedUploadStatus.STAGED || 
          f.status === ExtendedUploadStatus.COMMITTING
        );
        if (remainingFiles.length <= 1) { // <= 1 because this file will still be in the array
          userWantsToCommitRef.current = false;
          console.log('DEBUG - All files processed, reset userWantsToCommitRef to false');
        }
      } else {
        throw new Error(commitResponse.error?.message || 'Failed to commit file');
      }
    } catch (error: any) {
      console.error('Commit failed for file:', file.name, error);
      showError('Commit Failed', `Failed to commit ${file.name}: ${error.message}`);
      
      // Set file back to staged status on error and ensure it's tracked for cleanup
      setFiles((prev) =>
        prev.map((f) =>
          f.id === file.id ? { ...f, status: ExtendedUploadStatus.STAGED } : f
        )
      );
      
      // Add back to staged files for potential cleanup
      setStagedFiles(prev => new Set([...prev, file.azureFileName!]));
    }
  };

  const commitStagedFiles = async (): Promise<void> => {
    const stagedFilesList = files.filter(f => f.status === ExtendedUploadStatus.STAGED);
    
    console.log('commitStagedFiles - stagedFilesList:', stagedFilesList);
    
    if (stagedFilesList.length === 0) {
      showError('No Files', 'No staged files to commit');
      return;
    }

    // Commit each file individually
    for (const file of stagedFilesList) {
      await commitSingleFile(file);
    }
  };

  const cleanupStagedFiles = async (fileNames?: string[]): Promise<void> => {
    // Don't cleanup if files are being committed
    const hasCommittingFiles = files.some(f => f.status === ExtendedUploadStatus.COMMITTING);
    if (hasCommittingFiles && !fileNames) {
      console.log('DEBUG: Skipping cleanup - files are being committed');
      return;
    }

    const filesToCleanup = fileNames || Array.from(stagedFiles);
    
    if (filesToCleanup.length === 0) {
      return;
    }

    try {
      for (const azureFileName of filesToCleanup) {
        await uploadApi.cleanupStagedFile(azureFileName);
        setStagedFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(azureFileName);
          return newSet;
        });
      }
      console.log('Cleaned up staged files:', filesToCleanup);
    } catch (error: any) {
      console.error('Failed to cleanup staged files:', error);
      // Don't show error to user for cleanup failures
    }
  };

  const pendingFiles = files.filter((file) => file.status === ExtendedUploadStatus.PENDING);
  const stagingFiles = files.filter((file) => file.status === ExtendedUploadStatus.STAGING);
  const stagedFilesList = files.filter((file) => file.status === ExtendedUploadStatus.STAGED);
  const committingFiles = files.filter((file) => file.status === ExtendedUploadStatus.COMMITTING);
  const completedFiles = files.filter((file) => file.status === ExtendedUploadStatus.COMPLETED);
  const failedFiles = files.filter((file) => file.status === ExtendedUploadStatus.FAILED);
  
  // Legacy variables for compatibility (these should be removed after full migration)
  const uploadingFiles = committingFiles; // Map committing to uploading for display
  const allUploadingFiles = [...stagingFiles, ...committingFiles];
  
  const activeFiles = [...stagingFiles, ...committingFiles];
  const hasFilesToCommit = stagedFilesList.length > 0 || (stagingFiles.length > 0 && !userWantsToCommit);
  const hasUploadsToShow = pendingFiles.length > 0 || activeFiles.length > 0 || hasFilesToCommit;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload Files</h1>
        <p className="text-gray-600 mt-1">
          Securely upload files up to {formatFileSize(maxFileSize)} each. <strong>Uploads start automatically</strong> when files are selected. 
          Files are automatically compressed for optimal storage, except when already in zip format. All uploads are encrypted and logged.
        </p>
      </div>

      {/* Upload Status Indicator - only show if uploads are visible */}
      {showUploads && allUploadingFiles.length > 0 && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Loader2 className="w-5 h-5 text-brand-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-brand-900">
                {allUploadingFiles.length} file{allUploadingFiles.length !== 1 ? 's' : ''} uploading
              </p>
              <p className="text-xs text-brand-700">
                {pendingFiles.length > 0 && `${pendingFiles.length} more in queue • `}
                Uploads in progress
              </p>
            </div>
          </div>
        </div>
      )}

              {/* Background Staging Indicator - show when files are staging */}
        {!showUploads && (stagingFiles.length > 0 || stagedFilesList.length > 0) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-gray-400 rounded-full animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {stagingFiles.length > 0 
                    ? `${stagingFiles.length} file${stagingFiles.length !== 1 ? 's' : ''} preparing in background`
                    : `${stagedFilesList.length} file${stagedFilesList.length !== 1 ? 's' : ''} ready for upload`
                  }
                </p>
                <p className="text-xs text-gray-700">
                  {stagingFiles.length > 0 
                    ? 'Files are being pre-processed and staged'
                    : 'Click Upload to commit staged files to database'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Upload Button - show when there are files to upload and uploads aren't visible */}
      {!showUploads && hasUploadsToShow && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-brand-900">
                {files.length} file{files.length !== 1 ? 's' : ''} ready
              </p>
              <p className="text-xs text-brand-700">
                {stagingFiles.length > 0 && `${stagingFiles.length} staging • `}
                {stagedFilesList.length > 0 && `${stagedFilesList.length} ready • `}
                {stagingFiles.length > 0 && !userWantsToCommit 
                  ? 'Click to queue upload - files will commit automatically when staging completes'
                  : 'Click to commit and view results'
                }
              </p>
            </div>
            <button
              onClick={commitAndShowUploads}
              className="btn-primary"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </button>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      <div className="card">
        <div className="card-body">
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? 'active' : ''} ${
              files.some((f) => f.status === ExtendedUploadStatus.STAGING || f.status === ExtendedUploadStatus.COMMITTING) ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            <input {...getInputProps()} />
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              {isDragActive ? (
                <p className="text-lg text-brand-600 font-medium">Drop files to start uploading...</p>
              ) : (
                <>
                  <p className="text-lg text-gray-900 font-medium">
                    Drag and drop SQL Server backup files here, or click to select
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Accepted files: <span className="font-semibold">.bak files</span> and <span className="font-semibold">.zip archives</span> containing .bak files
                  </p>
                  <p className="text-sm text-gray-600">
                    Maximum file size: {formatFileSize(maxFileSize)}
                  </p>
                  <p className="text-xs text-brand-600 mt-1 font-medium">
                    ✨ Files are validated and uploads start automatically
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Smart Upload Info */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center space-x-3">
            <div className="text-brand-600">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">Smart Upload Detection</h3>
              <p className="text-sm text-gray-600">
                Upload method is automatically selected based on file size for optimal performance
              </p>
            </div>
          </div>
          
          <div className="mt-4 space-y-3">
            {/* Auto-start uploads toggle */}
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">Automatic Background Uploads</p>
                  <p className="text-xs text-green-600">Start uploading files immediately when dropped</p>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoStartUploads}
                  onChange={(e) => setAutoStartUploads(e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-green-700">
                  {autoStartUploads ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            {/* Smart upload method info */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="text-blue-600 mt-0.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">Automatic Upload Method Selection</p>
                  <ul className="mt-1 text-xs text-blue-700 space-y-1">
                    <li>• <strong>Small Files (&lt; 100MB):</strong> Standard upload with compression and processing</li>
                    <li>• <strong>Large Files (≥ 100MB):</strong> Direct chunked upload for maximum reliability</li>
                    <li>• <strong>Database Files:</strong> Optimized handling for .bak and .zip files up to 10GB</li>
                    <li>• <strong>Background Processing:</strong> Files start uploading immediately when dropped</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Files ({files.length} total)
                </h2>
                <p className="text-sm text-gray-600">
                  {showUploads ? (
                    <>
                      {uploadingFiles.length > 0 && `${uploadingFiles.length} uploading • `}
                      {pendingFiles.length > 0 && `${pendingFiles.length} queued • `}
                      {completedFiles.length > 0 && `${completedFiles.length} completed • `}
                      {failedFiles.length > 0 && `${failedFiles.length} failed`}
                    </>
                  ) : (
                    <>
                      {stagingFiles.length > 0 && `${stagingFiles.length} staging • `}
                      {stagedFilesList.length > 0 && `${stagedFilesList.length} ready • `}
                      {pendingFiles.length > 0 && `${pendingFiles.length} queued • `}
                      {completedFiles.length > 0 && `${completedFiles.length} completed • `}
                      {failedFiles.length > 0 && `${failedFiles.length} failed`}
                    </>
                  )}
                </p>
              </div>
              <div className="flex space-x-2">
                {/* Manual upload button when auto-start is disabled */}
                {!autoStartUploads && files.filter(f => f.status === ExtendedUploadStatus.PENDING).length > 0 && (
                  <button 
                    onClick={() => {
                      console.log('Manual upload triggered');
                      autoStartStaging();
                    }}
                    className="btn-primary text-sm"
                  >
                    Start Uploads
                  </button>
                )}
                {completedFiles.length > 0 && (
                  <button onClick={clearCompleted} className="btn-secondary text-sm">
                    Clear Completed
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-2xl relative">
                        {getFileTypeIcon(file.type, file.isCompressed)}
                        {file.compressionInfo?.isCompressing && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-brand-500 rounded-full animate-pulse"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                          </p>
                          {file.isCompressed && (
                            <Zap className="w-3 h-3 text-brand-500" />
                          )}
                          {file.wasOriginallyZip && (
                            <Archive className="w-3 h-3 text-gray-500" />
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <p className="text-xs text-gray-600">
                            {file.originalSize && file.originalSize !== file.size
                              ? `${formatFileSize(file.originalSize)} → ${formatFileSize(file.size)}`
                              : formatFileSize(file.size)
                            }
                          </p>
                          {file.spaceSaved && (
                            <span className="text-xs text-success-600 font-medium">
                              {file.spaceSaved} saved
                            </span>
                          )}
                          {/* Show upload method indicator */}
                          {(file.useDirect || (useDirectUpload && file.size > directUploadThreshold)) && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                              Direct Upload
                            </span>
                          )}
                          {file.size > directUploadThreshold && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                              Large File
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Status Icon */}
                      {file.status === ExtendedUploadStatus.COMPLETED && (
                        <CheckCircle className="w-5 h-5 text-success-500" />
                      )}
                      {file.status === ExtendedUploadStatus.FAILED && (
                        <AlertCircle className="w-5 h-5 text-error-500" />
                      )}
                      {/* Legacy UPLOADING status removed - using COMMITTING instead */}
                      {file.status === ExtendedUploadStatus.STAGING && (
                        <div className="w-5 h-5 bg-blue-400 rounded-full animate-pulse" title="Staging to Azure" />
                      )}
                      {file.status === ExtendedUploadStatus.STAGED && (
                        <div className="w-5 h-5 bg-green-400 rounded-full" title="Staged and ready to commit" />
                      )}
                      {file.status === ExtendedUploadStatus.COMMITTING && (
                        <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
                      )}
                      {file.status === ExtendedUploadStatus.PENDING && (
                        <div className="w-5 h-5 bg-gray-300 rounded-full" title="Ready to stage" />
                      )}
                      
                      {/* Actions */}
                      {file.status === ExtendedUploadStatus.FAILED && (
                        <button
                          onClick={() => retryFailedFile(file)}
                          className="text-xs btn-secondary py-1 px-2"
                        >
                          Retry
                        </button>
                      )}
                      
                      {(file.status === ExtendedUploadStatus.STAGING || file.status === ExtendedUploadStatus.COMMITTING) && (
                        <button
                          onClick={() => cancelUpload(file.id)}
                          className="text-xs btn-secondary py-1 px-2 text-error-600 hover:text-error-700"
                        >
                          Cancel
                        </button>
                      )}

                      {file.status === ExtendedUploadStatus.STAGED && (
                        <button
                          onClick={() => {
                            // Clean up staged file and remove from list
                            if (file.azureFileName) {
                              cleanupStagedFiles([file.azureFileName]);
                            }
                            removeFile(file.id);
                          }}
                          className="text-xs btn-secondary py-1 px-2 text-error-600 hover:text-error-700"
                        >
                          Discard
                        </button>
                      )}
                      
                      {file.status !== ExtendedUploadStatus.STAGING && file.status !== ExtendedUploadStatus.COMMITTING && file.status !== ExtendedUploadStatus.STAGED && (
                        <button
                          onClick={() => removeFile(file.id)}
                          className="text-gray-400 hover:text-error-500 transition-colors duration-200"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar - show for active operations when uploads are visible */}
                  {(file.status === ExtendedUploadStatus.STAGING || 
                    file.status === ExtendedUploadStatus.COMMITTING) && (
                    <div className="space-y-2">
                      <div className="progress-bar">
                        <div
                          className={`progress-fill progress-width-${Math.floor(file.progress / 10) * 10} ${
                            file.compressionProgress && file.compressionProgress < 100 
                              ? 'bg-gradient-to-r from-brand-500 to-brand-400' 
                              : ''
                          }`}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>
                          {file.processingStage || `${formatProgress(file.progress)}`}
                          {file.compressionProgress && file.compressionProgress < 100 && (
                            <span className="ml-2 text-brand-600">
                              (Compressing: {Math.round(file.compressionProgress)}%)
                            </span>
                          )}
                        </span>
                        {file.estimatedTimeRemaining && file.estimatedTimeRemaining > 0 && (
                          <span>{formatTimeRemaining(file.estimatedTimeRemaining)} remaining</span>
                        )}
                      </div>
                      
                      {/* Compression Progress */}
                      {file.compressionInfo?.isCompressing && file.compressionProgress && (
                        <div className="mt-2 p-2 bg-brand-50 border border-brand-200 rounded text-xs">
                          <div className="flex items-center space-x-2">
                            <Archive className="w-3 h-3 text-brand-500 animate-pulse" />
                            <span className="text-brand-700">
                              {file.compressionInfo.wasOriginallyZip 
                                ? 'File already compressed, skipping compression...'
                                : `Compressing file... ${formatProgress(file.compressionProgress)}`
                              }
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error Message */}
                  {file.status === ExtendedUploadStatus.FAILED && file.error && (
                    <div className="mt-2 p-2 bg-error-50 border border-error-200 rounded text-xs text-error-700">
                      {file.error}
                    </div>
                  )}

                  {/* Success Message */}
                  {file.status === ExtendedUploadStatus.COMPLETED && (
                    <div className="mt-2 p-2 bg-success-50 border border-success-200 rounded text-xs text-success-700">
                      <div className="flex items-center justify-between">
                        <span>Upload completed successfully</span>
                        {(file.isCompressed || file.wasOriginallyZip) && (
                          <span className="font-medium">
                            {getCompressionStatus(file)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload Statistics */}
      {files.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-900">{pendingFiles.length}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-success-600">{completedFiles.length}</p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-error-600">{failedFiles.length}</p>
              <p className="text-sm text-gray-600">Failed</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};